import { launchError, launchSuccess } from "components/notifications";
import { useEffect, useState } from "react";
import { fetchPartnerInformations } from "services/fetchUserById";
import { updatePartnerPercentages } from "services/userServices";
import { formatNumberWithSpaces } from "utils/numbers"
import { useTranslation } from 'react-i18next';
import { fetchPartnerCommissions, fetchPartnerKpi } from "services/partnerServices";
import './userPartner.css'


const UserPartner = ({ userData, user, id }) => {

  const { t } = useTranslation();

  const [partnerKpi, setPartnerKpi] = useState({});
  const [partnerCommissions, setPartnerCommissions] = useState({});
  const [partnerTimeframe, setPartnerTimeframe] = useState('all');
  const [partnerPercentages, setPartnerPercentages] = useState({});
  const [newPartnerPercentages, setNewPartnerPercentages] = useState({
    stocks: 0,
    leverage: 0,
    structured_products: 0
  });

  useEffect(() => {
    if (user && id && userData?.is_partner)
      getPartnerInfos()
  }, [user, id, userData?.is_partner])

  const getPartnerInfos = async () => {
    try {
      const data = await fetchPartnerInformations(user, id)
      setPartnerPercentages(data)
    } catch (error) {
      launchError(t('notifications.users.partner_infos.error'))
    }
  }

  const changePartnerPercentages = async () => {
    try {
      const isValid = Object.values(newPartnerPercentages).reduce((acc, value) => acc && value >= 0 && value <= 100, true);
      if (isValid) {
        const data = await updatePartnerPercentages(user, id, newPartnerPercentages);
        setPartnerPercentages(data);
        launchSuccess(t('notifications.users.userById.partner_percentages.success'))
      }
    } catch (error) {
      launchError(t('notifications.users.userById.partner_percentages.error'))
    }
  }

  const fetchPartnerData = async () => {
    try {
      const kpiPartner = await fetchPartnerKpi(user, id, partnerTimeframe);
      setPartnerKpi(kpiPartner)
    } catch (error) {
      console.error(error)
    }
  }

  const fetchPartnerCommissionsData = async () => {
    try {
      const commissionsPartner = await fetchPartnerCommissions(user, id, partnerTimeframe);
      setPartnerCommissions(commissionsPartner)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (userData?.is_partner) {
      fetchPartnerData();
      fetchPartnerCommissionsData();
    }
  }, [userData?.is_partner, partnerTimeframe])


  return <div className='partner_container'>
    <p className='partner_main_title'>Partner</p>

    {/* Rémunérations */}
    <div className='partner_section'>
      <p className='partner_section_title'>Rémunérations</p>
      <div className='partner_stats_grid'>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Stocks</span>
          <span className='partner_stat_value'>{partnerPercentages?.stocks ?? 0} %</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Leverage</span>
          <span className='partner_stat_value'>{partnerPercentages?.leverage ?? 0} %</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Produits structurés</span>
          <span className='partner_stat_value'>{partnerPercentages?.structured_products ?? 0} %</span>
        </div>
      </div>
      <div className='partner_update_row'>
        <div className='partner_input_group'>
          <label htmlFor='partner_stock_input' className='partner_input_label'>Stocks %</label>
          <input
            id='partner_stock_input'
            type='number'
            value={newPartnerPercentages?.stocks}
            className='partner_input'
            onChange={(e) => setNewPartnerPercentages(prev => ({ ...prev, stocks: Number(e.target.value) }))}
          />
        </div>
        <div className='partner_input_group'>
          <label htmlFor='partner_leverage_input' className='partner_input_label'>Leverage %</label>
          <input
            id='partner_leverage_input'
            type='number'
            value={newPartnerPercentages?.leverage}
            className='partner_input'
            onChange={(e) => setNewPartnerPercentages(prev => ({ ...prev, leverage: Number(e.target.value) }))}
          />
        </div>
        <div className='partner_input_group'>
          <label htmlFor='partner_structured_products_input' className='partner_input_label'>Produits structurés %</label>
          <input
            id='partner_structured_products_input'
            type='number'
            value={newPartnerPercentages?.structured_products}
            className='partner_input'
            onChange={(e) => setNewPartnerPercentages(prev => ({ ...prev, structured_products: Number(e.target.value) }))}
          />
        </div>
        <button className='partner_update_button' onClick={changePartnerPercentages}>Modifier</button>
      </div>
    </div>

    {/* KPI */}
    <div className='partner_section'>
      <div className='partner_section_header'>
        <p className='partner_section_title'>KPI</p>
        <div className='partner_timeframe_select'>
          <label htmlFor='partner_timeframe_select'>Timeframe</label>
          <select
            id='partner_timeframe_select'
            className='custom-select'
            onChange={(e) => setPartnerTimeframe(e.target.value)}
            value={partnerTimeframe}
          >
            <option value='all'>Tout temps</option>
            <option value='year'>Année</option>
            <option value='month'>Mois</option>
            <option value='week'>Semaine</option>
            <option value='day'>Jour</option>
          </select>
        </div>
      </div>
      <div className='partner_stats_grid'>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Total inscrits</span>
          <span className='partner_stat_value'>{partnerKpi?.subscribers?.count ?? 0} {partnerKpi?.subscribers?.performance ? `(${partnerKpi?.subscribers?.performance} %)` : ''}</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Total trader</span>
          <span className='partner_stat_value'>{partnerKpi.traders?.count ?? 0} {partnerKpi.traders?.performance ? `(${partnerKpi.traders?.performance} %)` : ''}</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Nombre de dépôts</span>
          <span className='partner_stat_value'>{partnerKpi.depositors?.count ?? 0} {partnerKpi.depositors?.performance ? `(${partnerKpi.depositors?.performance} %)` : ''}</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Total inscrits KYC</span>
          <span className='partner_stat_value'>{partnerKpi.kyc?.count ?? 0} {partnerKpi.kyc?.performance ? `(${partnerKpi.kyc?.performance} %)` : ''}</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Volume total</span>
          <span className='partner_stat_value'>{formatNumberWithSpaces(partnerKpi.volume?.count) ?? 0} €</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Total déposés</span>
          <span className='partner_stat_value'>{partnerKpi.deposits?.count ?? 0} {partnerKpi.deposits?.performance ? `(${partnerKpi.deposits?.performance} %)` : ''}</span>
        </div>
      </div>
    </div>

    {/* Commissions */}
    <div className='partner_section'>
      <p className='partner_section_title'>Commissions</p>
      <div className='partner_commission_total'>
        <span className='partner_stat_label'>Total</span>
        <span className='partner_commission_total_value'>{partnerKpi.commissions?.count ?? 0} €</span>
      </div>
      <div className='partner_stats_grid'>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Stocks</span>
          <span className='partner_stat_value'>{partnerCommissions?.stocks} €</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Playlist</span>
          <span className='partner_stat_value'>{partnerCommissions?.playlist} €</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Produits structurés</span>
          <span className='partner_stat_value'>{partnerCommissions?.structured_product} €</span>
        </div>
        <div className='partner_stat_item'>
          <span className='partner_stat_label'>Margin</span>
          <span className='partner_stat_value'>{partnerCommissions?.margin} €</span>
        </div>
      </div>
    </div>
  </div>
}

export default UserPartner
